/**
 * Shared settings schemas — Phase 2 Wave 1 [V2-FIX-04 / V2-FIX-10].
 *
 * The whitelist of SMTP fields is the single contract used by:
 *   - SettingsService (apps/api/src/settings/settings.service.ts) when
 *     resolving the SMTP config from the encrypted-at-rest store
 *   - MailService.sendWithConfig() (apps/api/src/mail/mail.service.ts)
 *   - MailDispatcher (apps/api/src/mail/mail-dispatcher.service.ts)
 *   - W5 Supervisor settings page (apps/web)
 *
 * Keeping the type here (instead of in @aris/api) lets the FE form
 * builder reuse the exact same shape without a duplicate definition
 * drifting over time.
 */
import { z } from 'zod';

/**
 * Validated SMTP config. `host` may be empty — that means "no transport
 * configured", which the MailService MUST handle by logging
 * `mail.skipped` (no PII, no token; see V2-FIX-04b).
 */
export const SmtpSettingsSchema = z
  .object({
    host: z.string().trim().max(255).default(''),
    port: z.coerce.number().int().min(1).max(65_535).default(587),
    user: z.string().trim().max(255).default(''),
    pass: z.string().max(1024).default(''),
    from: z.string().trim().email().max(255),
    secure: z.coerce.boolean().default(false),
  })
  .strict();
export type SmtpSettings = z.infer<typeof SmtpSettingsSchema>;

/** Alias matching the W1.T10 contract — supplied to MailService.sendWithConfig. */
export type SmtpConfig = SmtpSettings;
